/**
 * Thin HTTP client for the ScreenAtlas API. Port of `app/src/data/http.ts`
 * minus Sentry/Expo (web/docs/data-layer.md §2).
 *
 * - Attaches `Authorization: Bearer <access-token>` (see `tokenStore`).
 * - Attaches client-version metadata headers (`X-App-Version` / `X-App-Build`
 *   / `X-App-Platform: web`) centrally on every request — untrusted hints,
 *   never identity (docs/api-schema.md §Request metadata).
 * - On `401` it refreshes once (via `POST /auth/refresh`) and retries; a
 *   second `401` clears the session (the `onUnauthorized` handler bounces the
 *   UI to login).
 * - Non-2xx responses throw `ApiError` so callers can degrade on `404`.
 * - No retry/backoff beyond the single refresh retry. No offline queue.
 */

import { getLocale } from '@/i18n/locale';

import * as tokenStore from './auth/tokenStore';
import { API_BASE_URL } from './config';

export class ApiError extends Error {
  readonly status: number;
  readonly body: Record<string, unknown>;
  constructor(status: number, message: string, body: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = 'ApiError';
  }
}

export class UnauthorizedError extends ApiError {
  constructor() {
    super(401, 'Unauthorized');
    this.name = 'UnauthorizedError';
  }
}

export interface RequestOptions {
  /** Send the bearer token and apply the 401-refresh flow. Default true. */
  auth?: boolean;
  query?: Record<string, string | number | undefined>;
  body?: unknown;
}

/** Resolved once at import; stable across the session and the 401 retry. */
const clientMetadataHeaders: Record<string, string> = {
  'X-App-Version': __APP_VERSION__,
  'X-App-Build': __APP_BUILD__,
  'X-App-Platform': 'web',
};

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = path.startsWith('http') ? path : `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
  if (!query) return base;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${base}?${s}` : base;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const { auth = true, query, body } = opts;
  const url = buildUrl(path, query);

  const buildHeaders = async (): Promise<Record<string, string>> => {
    // Client-version metadata is attached centrally on every request (and the
    // single 401-refresh retry); callers cannot override it. The locale comes
    // from the module-level getLocale() that <LocaleProvider> keeps in sync.
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Accept-Language': getLocale(),
      ...clientMetadataHeaders,
    };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (auth) {
      const token = await tokenStore.getBearerToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  const init = (headers: Record<string, string>): RequestInit => ({
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let res = await fetch(url, init(await buildHeaders()));

  if (res.status === 401 && auth) {
    const refreshed = await tokenStore.refreshSession();
    if (refreshed) res = await fetch(url, init(await buildHeaders()));
    if (res.status === 401) {
      tokenStore.clearSession();
      throw new UnauthorizedError();
    }
  }

  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const detail = (json.error ?? json.message) as string | undefined;
    throw new ApiError(res.status, detail ?? `${method} ${path} -> ${res.status}`, json);
  }

  const text = await res.text();
  return (text ? (JSON.parse(text) as T) : (undefined as unknown as T));
}

export const apiGet = <T>(path: string, opts?: RequestOptions): Promise<T> =>
  request<T>('GET', path, opts);

export const apiPost = <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
  request<T>('POST', path, { ...opts, body });

export const apiPut = <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> =>
  request<T>('PUT', path, { ...opts, body });

export const apiDelete = <T>(path: string, opts?: RequestOptions): Promise<T> =>
  request<T>('DELETE', path, opts);
